import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import S from "./ImageWithHoverZoom.module.css";

/** Largest edge of the hover preview, in px. */
const MAX_ZOOM_SIZE = 400;

/** Gap between the cursor and the preview, and the minimum distance to a viewport edge. */
const CURSOR_OFFSET = 15;
const VIEWPORT_MARGIN = 10;

/** Size of the popup window opened on click. */
const POPUP_WIDTH = 1000;
const POPUP_HEIGHT = 800;

interface ImageWithHoverZoomProps {
  src: string;
  alt?: string;
  height?: number;
  isDashboard?: boolean;
}

/**
 * An image cell that shows a larger preview next to the cursor on hover, and
 * opens the full-size image in a popup window on click.
 *
 * The `data-testid` on the thumbnail is load-bearing: TableInteractive and
 * DataGrid look for it so that clicking an image does not also trigger the
 * cell's own click behaviour.
 */
export function ImageWithHoverZoom({
  src,
  alt = "",
  height = 30,
  isDashboard = false,
}: ImageWithHoverZoomProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!isHovering) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!imgRef.current) {
        return;
      }

      const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window;

      // Prefer below-right of the cursor, flipping to the other side when the
      // preview would overflow, and clamping so it never leaves the viewport.
      let x = e.clientX + CURSOR_OFFSET;
      let y = e.clientY + CURSOR_OFFSET;

      if (x + MAX_ZOOM_SIZE > viewportWidth) {
        x = e.clientX - MAX_ZOOM_SIZE - CURSOR_OFFSET;
      }
      if (y + MAX_ZOOM_SIZE > viewportHeight) {
        y = e.clientY - MAX_ZOOM_SIZE - CURSOR_OFFSET;
      }

      setZoomPosition({
        x: Math.max(x, VIEWPORT_MARGIN),
        y: Math.max(y, VIEWPORT_MARGIN),
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isHovering]);

  const handleClick = (e: ReactMouseEvent) => {
    // Keep the click away from the table's own cell handler.
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();

    const left = (window.screen.width - POPUP_WIDTH) / 2;
    const top = (window.screen.height - POPUP_HEIGHT) / 2;

    window.open(
      src,
      "ImagePopup",
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`,
    );
  };

  const handleMouseEnter = (e: ReactMouseEvent) => {
    e.stopPropagation();
    setIsHovering(true);
  };

  const handleMouseLeave = (e: ReactMouseEvent) => {
    e.stopPropagation();
    setIsHovering(false);
  };

  const thumbnailStyle: CSSProperties = {
    height,
    cursor: "pointer",
    position: "relative",
    zIndex: 1,
  };

  // Static chrome lives in the stylesheet; only what moves stays inline.
  const zoomContainerStyle: CSSProperties = {
    left: `${zoomPosition.x}px`,
    top: `${zoomPosition.y}px`,
    // Dashboards stack their own overlays, so the preview needs to clear them.
    zIndex: isDashboard ? 99999 : 10000,
  };

  const zoomedImageStyle: CSSProperties = {
    maxWidth: `${MAX_ZOOM_SIZE}px`,
    maxHeight: `${MAX_ZOOM_SIZE}px`,
    width: "auto",
    height: "auto",
    display: "block",
    borderRadius: "2px",
  };

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        style={thumbnailStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        title="Click to open full-size image in popup window"
        data-testid="image-with-hover-zoom"
      />
      {isHovering &&
        createPortal(
          <div
            className={S.zoomOverlay}
            style={zoomContainerStyle}
            data-testid="image-zoom-portal"
          >
            <img src={src} alt={alt} style={zoomedImageStyle} />
          </div>,
          document.body,
        )}
    </>
  );
}
